/**
 * Balance Coffee - Desktop nav ARM B controller + GA4 menu tracking.
 * Brief: CRO Hub "Desktop nav" (22 Sep 2026), items 4, 6 and 15.
 *
 * Self-contained: one custom element <bc-nav-b>. It does not use or touch the
 * theme's <desktop-navigation> (arm A), so the live menu behaves exactly as today.
 *
 *  - Opens on hover after 120ms (instant when moving between open panels),
 *    closes 200ms after the pointer leaves, so crossing the bar never flickers.
 *  - Click / Enter / Space toggle. ArrowDown or Tab moves into the open panel.
 *    Tab off the last link goes to the next top item. Escape closes and returns focus.
 *  - One panel at a time. The dim closes on click. Reduced motion = no fades (CSS).
 *  - Category rows swap the tiles on 150ms hover intent or on focus; reset to
 *    the first category on close.
 *  - GA4 (dataLayer): menu_open, nav_item_click, select_item (item_list_name "desktop_menu").
 *    Arm A gets nav_item_click too, so both arms can be compared in GA4.
 */
(function () {
  'use strict';

  var OPEN_DELAY = 120;
  var CLOSE_DELAY = 200;
  var CAT_INTENT = 150;

  function canHover() {
    return window.matchMedia && window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  }

  function currentArm() {
    var b = document.querySelector('.bc-nav-arm--b');
    if (b && window.getComputedStyle(b).display !== 'none') return 'b';
    return 'a';
  }

  function push(payload) {
    window.dataLayer = window.dataLayer || [];
    payload.device = 'desktop';
    payload.nav_arm = currentArm();
    window.dataLayer.push(payload);
  }

  function trackClick(el) {
    var label = el.getAttribute('data-ga-item') || (el.textContent || '').trim();
    var section = el.getAttribute('data-ga-section') || 'top_bar';
    var slot = parseInt(el.getAttribute('data-ga-slot'), 10) || null;

    push({ event: 'nav_item_click', item_label: label, section: section, slot: slot });

    if (el.hasAttribute('data-ga-product')) {
      window.dataLayer.push({ ecommerce: null });
      push({
        event: 'select_item',
        ecommerce: {
          item_list_id: 'desktop_menu',
          item_list_name: 'desktop_menu',
          items: [{
            item_id: el.getAttribute('data-ga-id') || undefined,
            item_name: label,
            index: slot,
            item_list_name: 'desktop_menu',
            item_category: section,
            price: parseFloat(el.getAttribute('data-ga-price')) || undefined
          }]
        }
      });
    }
  }

  if (window.customElements && !window.customElements.get('bc-nav-b')) {
    class BcNavB extends HTMLElement {
      connectedCallback() {
        if (this._ready) return;
        this._ready = true;
        this.current = null;
        this.openTimer = null;
        this.closeTimer = null;
        this.items = Array.prototype.slice.call(this.querySelectorAll('[data-panel-item]'));
        this.topLinks = Array.prototype.slice.call(this.querySelectorAll('.bc-nb__list > .bc-nb__item > .bc-nb__link'));

        this.items.forEach(this.bindItem.bind(this));
        this.bindPlainLinks();
        this.bindCategories();

        var self = this;
        this.querySelector('[data-bc-nb-close]').addEventListener('click', function () { self.hideAll(); });

        this.addEventListener('click', function (e) {
          var el = e.target.closest('a[data-ga-item]');
          if (el) trackClick(el);
        });

        document.addEventListener('keydown', function (e) {
          if (e.key === 'Escape' && self.current) {
            var btn = self.current.btn;
            self.hideAll();
            btn.focus();
          }
        });

        document.addEventListener('focusin', function (e) {
          if (self.current && !self.contains(e.target)) self.hideAll();
        });

        /* Theme editor: open the panel that holds the selected block */
        document.addEventListener('shopify:block:select', function (e) {
          var panel = e.target.closest && e.target.closest('.bc-nb__panel');
          if (!panel || !self.contains(panel)) return;
          var item = self.itemForPanel(panel);
          if (item) self.show(item);
          var cat = e.target.querySelector && e.target.querySelector('.bc-nb__cat');
          if (cat) self.setCat(panel, cat.getAttribute('data-cat'));
        });
      }

      itemForPanel(panel) {
        for (var i = 0; i < this.items.length; i++) {
          if (this.items[i]._panel === panel) return this.items[i];
        }
        return null;
      }

      bindItem(li) {
        var self = this;
        var btn = li.querySelector('button[aria-controls]');
        var panel = document.getElementById(btn.getAttribute('aria-controls'));
        if (!panel) return;
        li._panel = panel;
        li.btn = btn;
        li.panel = panel;

        li.addEventListener('mouseenter', function () {
          if (!canHover()) return;
          clearTimeout(self.closeTimer);
          clearTimeout(self.openTimer);
          self.openTimer = setTimeout(function () { self.show(li); }, self.current ? 0 : OPEN_DELAY);
        });
        li.addEventListener('mouseleave', function () { if (canHover()) self.scheduleClose(); });
        panel.addEventListener('mouseenter', function () { clearTimeout(self.closeTimer); });
        panel.addEventListener('mouseleave', function () { if (canHover()) self.scheduleClose(); });

        btn.addEventListener('click', function () {
          clearTimeout(self.openTimer);
          if (self.current === li) self.hide(li); else self.show(li);
        });

        btn.addEventListener('keydown', function (e) {
          if (e.key === 'ArrowDown') {
            e.preventDefault();
            self.show(li);
            self.focusFirst(panel);
          } else if (e.key === 'Tab' && !e.shiftKey && self.current === li) {
            /* panels sit after the top bar in the DOM: send Tab into the open panel */
            if (self.focusFirst(panel)) e.preventDefault();
          }
        });

        panel.addEventListener('keydown', function (e) {
          if (e.key !== 'Tab') return;
          var links = self.focusables(panel);
          if (!links.length) return;
          if (!e.shiftKey && document.activeElement === links[links.length - 1]) {
            var next = self.nextTop(btn);
            if (next) { e.preventDefault(); self.hide(li); next.focus(); }
          } else if (e.shiftKey && document.activeElement === links[0]) {
            e.preventDefault();
            btn.focus();
          }
        });
      }

      bindPlainLinks() {
        var self = this;
        this.topLinks.forEach(function (a) {
          if (a.tagName !== 'A') return;
          a.addEventListener('mouseenter', function () {
            if (!canHover()) return;
            clearTimeout(self.openTimer);
            self.hideAll();
          });
        });
      }

      bindCategories() {
        var self = this;
        Array.prototype.forEach.call(this.querySelectorAll('.bc-nb__panel[data-panel="shop"]'), function (panel) {
          var t = null;
          Array.prototype.forEach.call(panel.querySelectorAll('.bc-nb__cat'), function (cat) {
            cat.addEventListener('mouseenter', function () {
              clearTimeout(t);
              t = setTimeout(function () { self.setCat(panel, cat.getAttribute('data-cat')); }, CAT_INTENT);
            });
            cat.addEventListener('mouseleave', function () { clearTimeout(t); });
            cat.addEventListener('focus', function () { self.setCat(panel, cat.getAttribute('data-cat')); });
          });
        });
      }

      setCat(panel, key) {
        Array.prototype.forEach.call(panel.querySelectorAll('.bc-nb__tiles'), function (g) {
          g.classList.toggle('is-active', g.getAttribute('data-cat') === key);
        });
        Array.prototype.forEach.call(panel.querySelectorAll('.bc-nb__cat'), function (c) {
          c.classList.toggle('is-current', c.getAttribute('data-cat') === key);
        });
      }

      focusables(panel) {
        return Array.prototype.slice.call(panel.querySelectorAll('a[href], button:not([disabled])'))
          .filter(function (el) { return el.offsetParent !== null || el.getClientRects().length; })
          .filter(function (el) { return !el.closest('.bc-nb__tiles:not(.is-active)'); });
      }

      focusFirst(panel) {
        var f = this.focusables(panel)[0];
        if (!f) return false;
        /* wait one frame so visibility has flipped */
        requestAnimationFrame(function () { f.focus(); });
        return true;
      }

      nextTop(btn) {
        var i = this.topLinks.indexOf(btn);
        return i > -1 ? this.topLinks[i + 1] || null : null;
      }

      scheduleClose() {
        var self = this;
        clearTimeout(this.openTimer);
        clearTimeout(this.closeTimer);
        this.closeTimer = setTimeout(function () { self.hideAll(); }, CLOSE_DELAY);
      }

      show(li) {
        clearTimeout(this.closeTimer);
        if (this.current === li) return;
        if (this.current) this.hide(this.current, true);
        this.current = li;
        li.btn.setAttribute('aria-expanded', 'true');
        li.panel.classList.add('is-open');
        this.classList.add('has-open');
        push({ event: 'menu_open', menu_name: (li.btn.textContent || '').trim(), section: 'top_bar' });
      }

      hide(li, swapping) {
        li.btn.setAttribute('aria-expanded', 'false');
        li.panel.classList.remove('is-open');
        var self = this;
        var panel = li.panel;
        /* reset Shop to its first category once the fade has finished */
        setTimeout(function () {
          if (!panel.classList.contains('is-open') && panel.getAttribute('data-panel') === 'shop') self.setCat(panel, '1');
        }, CLOSE_DELAY);
        if (this.current === li) {
          this.current = null;
          if (!swapping) this.classList.remove('has-open');
        }
      }

      hideAll() {
        if (this.current) this.hide(this.current);
        this.classList.remove('has-open');
      }
    }

    window.customElements.define('bc-nav-b', BcNavB);
  }

  /* Arm A: same nav_item_click event so both arms read side by side in GA4.
     Delegated listener only - no change to arm A's markup or behaviour. */
  document.addEventListener('click', function (e) {
    var a = e.target.closest && e.target.closest('.bc-nav-arm--a a[href]');
    if (!a) return;
    var top = a.closest('.header__linklist-item');
    push({
      event: 'nav_item_click',
      item_label: (a.textContent || '').replace(/\s+/g, ' ').trim(),
      section: a.classList.contains('header__linklist-link') ? 'top_bar' : (top ? top.getAttribute('data-item-title') : 'menu'),
      slot: null
    });
  });
})();
